/**
 * Діалог «Є незбережені зміни» (UX-27): лише для форм, де чернетки бути не може (адмін-форми,
 * поля `sensitive`), або коли чернетку не вдалося записати. Три дії: «Зберегти», «Не зберігати»,
 * «Залишитися». `Enter` — «Зберегти» (вона у фокусі при відкритті), `Esc` — «Залишитися».
 *
 * Нативний `<dialog>` через `showModal()`: фокус усередині, решта сторінки інертна, `Esc` дає подію
 * `cancel`. Після закриття фокус повертається туди, де був (components.md, розд. 9).
 */
import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { save as t } from '../../i18n/uk/save.ts';

interface Props {
  /** `null` — у формі немає збереження без людини (лише «Не зберігати» і «Залишитися»). */
  onSave: (() => void) | null;
  onDiscard: () => void;
  onStay: () => void;
  /** Збереження з діалогу триває. */
  busy?: boolean;
  /** Свої тексти (діалог виходу): типово — «незбережені зміни» переходу. */
  text?: { title: string; body: string; stay: string; discard: string };
}

export function UnsavedDialog({ onSave, onDiscard, onStay, busy = false, text }: Props) {
  const ids = useId();
  const words = text ?? {
    title: t.unsavedTitle,
    body: t.unsavedText,
    stay: t.unsavedStay,
    discard: t.unsavedDiscard,
  };
  const ref = useRef<HTMLDialogElement>(null);
  const [returnTo] = useState(() => document.activeElement as HTMLElement | null);
  const stay = useRef(onStay);
  stay.current = onStay;

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (!dialog.open) dialog.showModal();
    dialog.querySelector<HTMLButtonElement>('[data-autofocus]')?.focus();
    const onCancel = (e: Event) => {
      e.preventDefault();
      stay.current();
    };
    dialog.addEventListener('cancel', onCancel);
    return () => {
      dialog.removeEventListener('cancel', onCancel);
      if (dialog.open) dialog.close();
      if (returnTo?.isConnected) returnTo.focus();
    };
  }, [returnTo]);

  return createPortal(
    <dialog
      ref={ref}
      className="dialog unsaved-dialog"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby={`${ids}-t`}
      aria-describedby={`${ids}-d`}
      data-testid="unsaved-dialog"
      onKeyDown={(e) => {
        // `Esc` — «Залишитися». preventDefault знімає й подію `cancel`, тож дія одна.
        if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
          onStay();
        }
      }}
    >
      <div className="dialog__head">
        <h2 className="dialog__title" id={`${ids}-t`}>
          {words.title}
        </h2>
      </div>
      <div className="dialog__body">
        <p id={`${ids}-d`}>{words.body}</p>
      </div>
      <div className="dialog__foot">
        <button
          className="btn"
          type="button"
          onClick={onStay}
          {...(onSave ? {} : { 'data-autofocus': '' })}
        >
          {words.stay}
        </button>
        <button className="btn" type="button" onClick={onDiscard}>
          {words.discard}
        </button>
        {onSave && (
          <button
            className="btn btn--primary"
            type="button"
            data-autofocus=""
            aria-busy={busy || undefined}
            aria-disabled={busy || undefined}
            onClick={() => {
              if (!busy) onSave();
            }}
          >
            {t.unsavedSave}
          </button>
        )}
      </div>
    </dialog>,
    document.body,
  );
}
